import { API_ROOT, BUNGIE_API_KEY, BUNGIE_ROOT, STATS_ROOT } from './config';

/** Bungie answered, but with an error of its own (ErrorCode is not 1 = Success). */
export class BungieError extends Error {
  /** which of several calls failed, where a caller names them */
  step?: string;
  constructor(public code: number, public status: string, message: string, public data?: Record<string, string>) {
    super(message);
  }
}

interface Envelope<T> {
  Response: T;
  ErrorCode: number;
  ErrorStatus: string;
  Message: string;
  MessageData?: Record<string, string>;
}

/**
 * One request to Bungie's API (`path` starts after /Platform, e.g. "/Destiny2/Milestones/"). With a token it reads
 * the signed-in player's private things too.
 */
export async function bungie<T>(path: string, opts: { token?: string; method?: 'GET' | 'POST'; body?: unknown; signal?: AbortSignal; stats?: boolean } = {}): Promise<T> {
  const headers: Record<string, string> = { 'X-API-Key': BUNGIE_API_KEY };
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  const resp = await fetch(`${opts.stats ? STATS_ROOT : API_ROOT}/Platform${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
    cache: opts.token ? 'no-store' : undefined,   // a signed-in read is the player's live state (just equipped, just moved): never an old copy
  });
  let env: Envelope<T>;
  try {
    env = await resp.json();
  } catch {
    throw new BungieError(resp.status, 'HttpError', `Bungie answered ${resp.status}`);
  }
  if (env.ErrorCode !== 1) throw new BungieError(env.ErrorCode, env.ErrorStatus, env.Message, env.MessageData);
  return env.Response;
}

/** A Bungie image path ("/common/destiny2_content/...") as a full URL. */
export const img = (path?: string) => (path ? `${BUNGIE_ROOT}${path}` : undefined);

/** The same picture served from this site's own origin (for CSS masks and canvas, which need it). */
export const sameOriginImg = (path?: string) => (path ? (API_ROOT === BUNGIE_ROOT ? `/api/bimg${path}` : `${API_ROOT}${path}`) : undefined);

// ---------------- the few shapes used so far ----------------

export interface UserInfoCard {
  membershipType: number;
  membershipId: string;
  displayName: string;
  bungieGlobalDisplayName?: string;
  bungieGlobalDisplayNameCode?: number;
  crossSaveOverride: number;
  iconPath?: string;
}

export interface MembershipsResponse {
  destinyMemberships: UserInfoCard[];
  primaryMembershipId?: string;
  bungieNetUser: { uniqueName?: string; displayName: string; profilePicturePath?: string };
}

export interface CharacterComponent {
  characterId: string;
  classType: number;
  raceType: number;
  light: number;
  emblemPath: string;
  emblemBackgroundPath: string;
  dateLastPlayed: string;
  minutesPlayedTotal: string;
}

export interface ProfileResponse {
  profile?: { data?: { userInfo: UserInfoCard; characterIds: string[]; dateLastPlayed: string } };
  characters?: { data?: Record<string, CharacterComponent> };
}

/**
 * The Destiny account to show: the one Cross Save points at, else Bungie's primary one, else the first. A player with
 * Cross Save has several memberships but plays on one.
 */
export function pickMembership(m: MembershipsResponse): UserInfoCard | undefined {
  const list = m.destinyMemberships;
  return (
    list.find((x) => x.crossSaveOverride !== 0 && x.crossSaveOverride === x.membershipType) ??
    list.find((x) => x.membershipId === m.primaryMembershipId) ??
    list[0]
  );
}

export const CLASS_NAMES = ['Titan', 'Hunter', 'Warlock'];
export const RACE_NAMES = ['Human', 'Awoken', 'Exo'];

export const bungieName = (u: UserInfoCard) =>
  u.bungieGlobalDisplayName
    ? `${u.bungieGlobalDisplayName}#${String(u.bungieGlobalDisplayNameCode ?? 0).padStart(4, '0')}`
    : u.displayName;
